import type { DataRouteMatch, DataRouter, Location, Params, RouterState } from 'react-router';
import { trackError } from '@gonfalon/telemetry/trackError';

export type RouteViewArgs = {
  params: Params;
  location: Location;
};

export type TrackRouteView = (args: RouteViewArgs) => void;

function isTrackRouteView(value: unknown): value is TrackRouteView {
  return typeof value === 'function';
}

function getTrackView(match: DataRouteMatch): TrackRouteView | undefined {
  const handle: unknown = match.route.handle;
  if (typeof handle !== 'object' || handle === null || !('trackView' in handle)) {
    return undefined;
  }
  return isTrackRouteView(handle.trackView) ? handle.trackView : undefined;
}

function erroredIndex(state: RouterState) {
  if (!state.errors) {
    return -1;
  }
  return state.matches.findIndex((match) => state.errors?.[match.route.id] !== undefined);
}

function pathParamNames(path: string | undefined) {
  return (path ?? '')
    .split('/')
    .filter((segment) => segment.startsWith(':') || segment === '*')
    .map((segment) => (segment === '*' ? '*' : segment.slice(1).replace(/\?$/, '')));
}

function viewKey(matches: DataRouteMatch[], index: number) {
  const names = matches.slice(0, index + 1).flatMap((match) => pathParamNames(match.route.path));
  const { params } = matches[index];
  return JSON.stringify(names.map((name) => [name, params[name] ?? null]));
}

function safelyTrackView(trackView: TrackRouteView, args: RouteViewArgs, routeId: string) {
  try {
    trackView(args);
  } catch (error) {
    trackError(error instanceof Error ? error : new Error(String(error)), {
      severity: 'low',
      owner: 'team-foundation',
      metadata: { routeId },
    });
  }
}

export function subscribeToRouteViews(router: DataRouter) {
  let viewed = new Map<string, string>();
  let lastLocationKey: string | undefined;

  const evaluate = (state: RouterState) => {
    if (!state.initialized || state.navigation.state !== 'idle') {
      return;
    }

    const sameLocation = state.location.key === lastLocationKey;
    lastLocationKey = state.location.key;
    const errored = erroredIndex(state);
    const next = new Map<string, string>();

    state.matches.forEach((match, index) => {
      const trackView = getTrackView(match);
      if (!trackView) {
        return;
      }
      if (errored !== -1 && index >= errored) {
        const previous = viewed.get(match.route.id);
        if (sameLocation && previous !== undefined) {
          next.set(match.route.id, previous);
        }
        return;
      }
      const key = viewKey(state.matches, index);
      next.set(match.route.id, key);
      if (viewed.get(match.route.id) !== key) {
        safelyTrackView(trackView, { params: match.params, location: state.location }, match.route.id);
      }
    });
    viewed = next;
  };

  evaluate(router.state);
  return router.subscribe(evaluate);
}
